import type { Translator } from "types";
import requestWithAuth from "../httpClient";
import { rootURL } from "../rootURL";

const clientsURL = `${rootURL}clients/`;
export type Client = {
	_id: string;
	name: string;
	surname: string;
	translators: Translator[];
};
export function getClientsRequest({
	noImageParams = false,
	searchQuery = "",
	shouldFillTranslators = false,
}) {
	let queryParams = "";
	if (noImageParams) {
		queryParams = `?noImageParams=${noImageParams}`;
	}
	if (shouldFillTranslators) {
		queryParams += `${queryParams ? "&" : "?"}shouldFillTranslators=${shouldFillTranslators}`;
	}
	if (searchQuery) {
		queryParams += `${queryParams ? "&" : "?"}searchQuery=${encodeURIComponent(searchQuery)}`;
	}
	return requestWithAuth<Client[]>("get", `${clientsURL}get/${queryParams}`);
}

export async function fetchClientsList(search = ""): Promise<Client[]> {
	const response = await getClientsRequest({
		noImageParams: true,
		searchQuery: search,
	});
	if (response.status !== 200) {
		throw new Error("Something went wrong with getting clients");
	}
	return response.body;
}

/** The whole client, as the form assembles it. Was declared as a string. */
export function addClient(client: ClientPayload) {
	return requestWithAuth("post", `${clientsURL}add/`).send(client);
}

export function removeClient(id: string) {
	return requestWithAuth("delete", `${clientsURL}${id}`);
}

export function updateClient(editedClient: ClientPayload) {
	return requestWithAuth("put", `${clientsURL}${editedClient._id}`).send(
		editedClient,
	);
}

/**
 * What GET /clients/overview answers with — the whole card, minus the figures,
 * which arrive separately from the finance summaries. Four of these fields were
 * missing from this type while the card read them regardless.
 */
/**
 * What the client form sends when creating or updating one. Stated on its own
 * rather than as a Partial of the card shape: the form carries the site logins
 * whole and does not know about populated translators.
 */
export type ClientPayload = {
	_id?: string;
	name?: string;
	surname?: string;
	image?: string | null;
	bankAccount?: string;
	instagramLink?: string;
	suspended?: boolean;
	svadba?: { login?: string; password?: string };
	dating?: { login?: string; password?: string };
	suspendedTranslators?: string[];
	translators?: unknown[];
};

export type ClientOverview = {
	_id: string;
	name: string;
	surname: string;
	image?: string | null;
	bankAccount?: string;
	svadba?: { login?: string; password?: string };
	dating?: { login?: string; password?: string };
	/** Already expanded to a full URL by the server, or an empty string. */
	instagramLink?: string;
	suspended?: boolean;
	suspendedTranslators?: string[];
	translators?: Array<{ _id: string; name?: string; surname?: string }>;
};

export async function getClientsOverviewRequest(): Promise<ClientOverview[]> {
	const response = await requestWithAuth<ClientOverview[]>(
		"get",
		`${clientsURL}overview`,
	);
	if (response.status === 200) {
		return response.body;
	}
	throw new Error("Something went wrong with clients data");
}

// Lightweight fetch for Schedule page: only basic clients, no images/calculations
export async function getClientsForSchedulesRequest() {
	const res = await getClientsRequest({
		noImageParams: true,
		shouldFillTranslators: true,
	});
	if (res.status === 200 || res.ok) {
		return res.body as Client[];
	}
	throw new Error("Failed to fetch clients for schedules");
}

export async function getClientsImagesRequest(ids?: string[]) {
	const query = ids?.length ? `?ids=${encodeURIComponent(ids.join(","))}` : "";
	const res = await requestWithAuth("get", `${clientsURL}images${query}`);
	if (res.status === 200) {
		return res.body as Array<{ _id: string; image?: string | null }>;
	}
	throw new Error("Failed to fetch client images");
}

export async function getClientImageRequest(clientId: string) {
	const res = await requestWithAuth(
		"get",
		`${clientsURL}images?ids=${encodeURIComponent(clientId)}`,
	);
	if (res.status === 200) {
		const arr = res.body as Array<{ _id: string; image?: string | null }>;
		return arr?.[0]?.image ?? null;
	}
	throw new Error("Failed to fetch client image");
}

export type ClientFinanceSummary = {
	currentMonthTotalAmount: number;
	previousMonthTotalAmount: number;
	twoMonthBeforeAmount: number;
	middleMonthSum: number;
	// Star count derived server-side from middleMonthSum.
	rating: number;
	previousMiddleMonthSum: number;
	allYearsProfit: number;
	totalPayments: number;
	/** This month against the same days of the previous one, in per cent. */
	monthProgressPercent: number;
};

export async function getClientFinanceSummaryRequest(clientId: string) {
	const res = await requestWithAuth(
		"get",
		`${clientsURL}${clientId}/finance-summary`,
	);
	if (res.status === 200) {
		return res.body as ClientFinanceSummary;
	}
	throw new Error("Failed to fetch client finance summary");
}

// Summaries for many clients in one request, keyed by client id. Omit the ids
// to get every client, which is what the list wants: passing 123 ObjectIds
// builds a ~3KB URL and, worse, ties the cache key to the filtered list so
// typing in the search box would refetch everything.
export async function getClientsFinanceSummariesRequest(ids?: string[]) {
	const query = ids?.length ? `?ids=${encodeURIComponent(ids.join(","))}` : "";
	const res = await requestWithAuth(
		"get",
		`${clientsURL}finance-summaries${query}`,
	);
	if (res.status === 200) {
		return res.body as Record<string, ClientFinanceSummary>;
	}
	throw new Error("Failed to fetch client finance summaries");
}
